import { useEffect, useState } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { Box, useMediaQuery } from '@mui/material'

import { RowBox } from '@components/shared/RowBox'
import { Input } from '@components/shared/Input'
import { EditIcon } from '@assets/svgs/EditIcon'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { setInitialValues } from './helpers/setInitialValues'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { Textarea } from '@components/shared/Textarea'
import { PlusCircleIcon } from '@assets/svgs/PlusCircleIcon'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { Select } from '@components/shared/Select'
import { editDetailedSummaryExpensesModalSchema } from './form/validations'
import { useOtherExpenses } from '@pages/OtherExpenses/context'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { useUser } from '@hooks/useUser'

import type { EditDetailedSummaryExpensesModalFormProps } from './form/types'
import type { EditDetailedSummaryExpensesModalProps } from './types'

import * as S from './styles'

export const EditDetailedSummaryExpenses = ({
  data,
  open,
  onClose,
  canEdit = true,
  ...rest
}: EditDetailedSummaryExpensesModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData } = useUser()

  const {
    detailedSummaryExpensesData,
    isDetailedSummaryExpensesLoading,
    isEditDetailedSummaryExpensesLoading,
    editDetailedSummaryExpenses
  } = useOtherExpenses()

  const methods = useForm<EditDetailedSummaryExpensesModalFormProps>({
    defaultValues: initialFormValues,
    resolver: editDetailedSummaryExpensesModalSchema as unknown as Resolver<EditDetailedSummaryExpensesModalFormProps>
  })

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)

  const accountDescriptionOptions = detailedSummaryExpensesData?.availableFilters?.expenseTypes
    ?.map((option) => ({
      label: option.description,
      value: option.accountingCode
    }))
    .filter((option) => option.value !== 'Todos')

  const onError = (errors: FieldErrors<EditDetailedSummaryExpensesModalFormProps>) => console.log({ errors })

  const onSubmit = (formData: EditDetailedSummaryExpensesModalFormProps) => {
    if (!canEdit) return

    formData.filiCode = filiData?.filiCode!
    formData.competenceDate = filiData?.startPeriod!

    editDetailedSummaryExpenses(formData).then((success) => success && onClose(false))
  }

  const isEdit = !!data

  useEffect(() => {
    !!data && methods.reset(setInitialValues(data!))
  }, [data, open])

  useEffect(() => {
    !open && methods.reset(initialFormValues)
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        heightContainer={isMobile ? 'calc(100% - 20px)' : 'auto '}
        title={isEdit ? 'Editar Resumo Detalhado' : 'Adicionar Resumo Detalhado'}
        headerIcon={isEdit ? <EditIcon /> : <PlusCircleIcon />}
        onClose={() => setOpenCancelDetailedSummaryModal(true)}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Confirmar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit, onError)() : undefined,
          loading: isEditDetailedSummaryExpensesLoading
        }}
        {...rest}
      >
        <S.Title>Preencha todos os campos antes de confirmar a edição do Resumo Detalhado.</S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={4}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <Select
                  required
                  name="accountDescription"
                  label="Descrição Conta"
                  placeholder="Selecione a desc. da conta"
                  options={!!accountDescriptionOptions?.length ? accountDescriptionOptions : []}
                  onChange={({ target }) => {
                    const accountDescriptionText = accountDescriptionOptions?.find(
                      (option) => option.value === target.value
                    )?.label

                    methods.setValue('accountDescription', accountDescriptionText as string)
                    methods.setValue('accountCode', target.value as string)
                  }}
                  loading={isDetailedSummaryExpensesLoading}
                  disabled={!canEdit || isEditDetailedSummaryExpensesLoading}
                />
              </Box>
              <Input name="accountCode" label="Conta" placeholder="Nº da Conta" required disabled />
              <Input
                required
                name="otherExpensesAmount"
                label="Valor Outras Despesas"
                placeholder="R$ 0,00"
                maskType="currency"
                onBlur={({ target }) => {
                  methods.setValue('otherExpensesAmount', unMaskCurrency(target.value).toFixed(2))
                }}
                disabled={!canEdit || isEditDetailedSummaryExpensesLoading}
              />
            </RowBox>

            <RowBox>
              <Textarea
                required
                name="observations"
                label="Observações"
                placeholder="Descreva as suas observações"
                disabled={!canEdit || isEditDetailedSummaryExpensesLoading}
              />
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />
    </>
  )
}
